<div class="available-promotions">
  <!-- Promo code input (stacking rules apply uniformly to manual codes) -->
  <div class="promo-code" [class.promo-code--disabled]="isUpdating()">
    <span class="material-symbols-outlined promo-code__icon">sell</span>
    <input
      type="text"
      class="promo-code__input"
      placeholder="Have a promo code? Enter code here"
      [ngModel]="promoCode()"
      (ngModelChange)="promoCode.set($event)"
      [disabled]="isUpdating()"
      aria-label="Have a promo code? Enter code here"
      (keyup.enter)="applyPromoCode()"
    />
    <button
      type="button"
      class="btn btn-secondary promo-code__apply"
      [disabled]="isUpdating() || !promoCode().trim()"
      (click)="applyPromoCode()"
    >
      Apply
    </button>
  </div>

  <!-- Applied promotions and coupons — removable from here -->
  @if (appliedCoupons().length) {
  <div class="applied-coupons">
    <p class="applied-coupons__title">Applied promotions</p>
    <div class="applied-coupons__list">
      @for (coupon of appliedCoupons(); track couponId(coupon)) {
      <div
        class="applied-coupons__item animated fadeIn"
        [class.applied-coupons__item--pending]="isPendingRemoval(coupon)"
        [class.applied-coupons__item--promotion]="isPromotionEntry(coupon)"
      >
        @if (isPendingRemoval(coupon)) {
        <span class="material-symbols-outlined applied-coupons__icon applied-coupons__icon--muted">timelapse</span>
        <span class="applied-coupons__label applied-coupons__label--removed">Promotion removed</span>
        <button
          type="button"
          class="applied-coupons__undo"
          (click)="undoRemoveCoupon(coupon)"
        >
          <span class="material-symbols-outlined">undo</span> Undo ({{ pendingSeconds(coupon) }}s)
        </button>
        } @else {
        <span class="material-symbols-outlined applied-coupons__icon">
          {{ isPromotionEntry(coupon) ? 'local_offer' : 'confirmation_number' }}
        </span>
        <span class="applied-coupons__label">{{ couponLabel(coupon) }}</span>
        @if (coupon.amount) {
        <span class="applied-coupons__amount">{{ coupon.amount | currency }}</span>
        }
        <button
          type="button"
          class="applied-coupons__remove"
          [disabled]="couponRemovingId() !== null"
          (click)="requestRemoveCoupon(coupon)"
          [attr.aria-label]="'Remove ' + couponLabel(coupon)"
        >
          @if (couponRemovingId() === couponId(coupon)) {
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          } @else {
          <span class="material-symbols-outlined">close</span>
          }
        </button>
        }
      </div>
      }
    </div>
  </div>
  }

  @if (showCouponError()) {
  <div class="alert alert-warning animated fadeIn py-2 px-3 mb-3" role="alert">
    {{ couponErrorMessage() }}
  </div>
  }

  @if (errorMessage()) {
  <div class="alert alert-warning animated fadeIn py-2 px-3 mb-3" role="alert">
    {{ errorMessage() }}
  </div>
  }

  <h3 class="available-promotions__heading">Available promotions</h3>

  @if (!promotionViews().length) {
  <p class="text-center text-black-50 my-4">There are no promotions available right now.</p>
  }

  <!-- Promotion cards: 2-up grid on desktop, full width when products are expanded -->
  <div class="row g-3 promo-list">
  @for (view of promotionViews(); track trackByPromotion($index, view)) {
  <div class="col-12 d-flex" [class.col-lg-6]="expandedProductsId() !== view.promotion.id">
  <div
    class="promo-card animated fadeIn w-100 h-100"
    [class.promo-card--selected]="view.promotion.isApplied"
  >
    <!-- Top row: promotion info + select/remove action (always aligned together) -->
    <div class="promo-card__top">
      <div class="promo-card__body">
        <div class="promo-card__header">
          <h4 class="promo-card__title">{{ view.title }}</h4>
          @if (view.promotion.isStackable) {
          <span class="badge bg-success-subtle text-success-emphasis">Stackable</span>
          }
        </div>

        @if (view.promotion.description) {
        <p class="promo-card__description">{{ view.promotion.description }}</p>
        }

        <!-- Requirements: "Requirements: <phrase>. <phrase>" (DEV-10937). -->
        @if (view.requirementsText) {
        <p class="promo-card__conditions">{{ view.requirementsText }}</p>
        }

        <!-- Expiration: only when the backend sends `expiresAt` (DEV-10937). -->
        @if (view.expiresText) {
        <p class="promo-card__expires">{{ view.expiresText }}</p>
        }

        <!--
          ALTERNATIVE "Option A" layout: one structured row per requirement, list
          conditions as chips (capped at 3 + "+N"). Kept commented so we can switch
          to it after reviewing with real data — DEV-9447. To use it: comment out
          the `.promo-card__conditions` line above and uncomment this block. (Its
          data comes from `view.requirements` / `getPromotionRequirements`, still
          computed in the util; the `.promo-req` / `.promo-chip` styles remain in
          the SCSS.)

        @if (view.requirements.length) {
        <ul class="promo-card__requirements">
          @for (req of view.requirements; track $index) {
          <li class="promo-req" [class.promo-req--met]="req.met">
            <span class="material-symbols-outlined promo-req__icon">
              {{ requirementIcon(req.icon) }}
            </span>
            <span class="promo-req__body">
              <span class="promo-req__label">
                {{ req.label }}
                @if (req.labelSuffix) {
                <span class="promo-req__suffix">{{ req.labelSuffix }}</span>
                }
              </span>
              @if (req.chips?.length) {
              <span class="promo-req__chips">
                @for (chip of req.chips; track chip) {
                <span class="promo-chip" [class]="'promo-chip--' + (req.chipKind || 'product')">{{ chip }}</span>
                }
                @if (req.overflowCount) {
                <span class="promo-chip promo-chip--more">+{{ req.overflowCount }}</span>
                }
              </span>
              }
            </span>
            <span class="promo-req__state">
              @if (req.met) {
              <span class="material-symbols-outlined promo-req__check">check</span>
              } @else if (req.pendingText) {
              <span class="promo-req__pending">{{ req.pendingText }}</span>
              }
            </span>
          </li>
          }
        </ul>
        }
        -->
      </div>

      <div class="promo-card__action">
        @if (view.promotion.isApplied) {
        <button
          type="button"
          class="btn btn-sm btn-primary promo-card__select"
          [disabled]="isUpdating()"
          (click)="remove(view.promotion)"
          aria-label="Remove promotion"
        >
          @if (loadingId() === view.promotion.id) {
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          } @else {
          <span class="material-symbols-outlined">check</span> Selected
          }
        </button>
        } @else {
        <button
          type="button"
          class="btn btn-sm promo-card__select"
          [class.btn-outline-secondary]="view.isMet"
          [class.btn-light]="!view.isMet"
          [disabled]="!view.isMet || isUpdating()"
          [attr.aria-disabled]="!view.isMet"
          [attr.aria-label]="view.isMet ? 'Select promotion' : view.disabledReason"
          (click)="select(view)"
        >
          @if (loadingId() === view.promotion.id) {
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          } @else {
          Select
          }
        </button>
        }
      </div>
    </div>

    <!-- Progress (when not yet met) -->
    @if (!view.isMet && view.progressText) {
    <div class="promo-card__progress">
      <div class="promo-card__progress-bar" role="progressbar" [attr.aria-valuenow]="view.progressPercent"
        aria-valuemin="0" aria-valuemax="100">
        <span class="promo-card__progress-fill" [style.width.%]="view.progressPercent"></span>
      </div>
      <small class="promo-card__progress-text">{{ view.progressText }}</small>
    </div>
    }

    <!-- Products to add (lazy-loaded, full width, rendered as the search product cards) -->
    @if (hasProductsCondition(view.promotion)) {
    <div class="promo-card__products-area">
      <button
        type="button"
        class="btn btn-link btn-sm p-0 promo-card__products-toggle"
        (click)="toggleProducts(view.promotion)"
        [disabled]="productsLoadingId() === view.promotion.id"
      >
        @if (productsLoadingId() === view.promotion.id) {
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Loading products…
        } @else if (expandedProductsId() === view.promotion.id) {
        Hide products
        } @else {
        View required products to add
        }
      </button>

      @if (expandedProductsId() === view.promotion.id) {
      <div class="promo-card__products animated fadeIn">
        @for (product of expandedProducts(); track product.id) {
        <app-autocomplete-product-card
          [product]="product"
          [hasSession]="true"
          [isLoading]="addingProductId() === product.id"
          (productClick)="onProductClick($event)"
          (addToCartClick)="onProductAddToCart($event)"
        ></app-autocomplete-product-card>
        } @empty {
        <p class="text-black-50 small m-0">No products to show.</p>
        }
      </div>

      @if (categoryLink(view.promotion); as link) {
      <button type="button" class="btn btn-link btn-sm p-0 promo-card__view-all" (click)="goToCategory(view.promotion)">
        View all in {{ link.name }}
        <span class="material-symbols-outlined">arrow_forward</span>
      </button>
      }
      }
    </div>
    }
  </div>
  </div>
  }
  </div>
</div>
